/**
 * JSON Path utilities for reading/writing values at arbitrary dot-separated paths
 * within a workflow definition. This allows node-type-agnostic operations —
 * the same functions work for actions, triggers, tools, nested scoped actions, etc.
 * 
 * Paths use dot notation, e.g.:
 *   "definition.triggers.manual"
 *   "definition.actions.Validate_File"
 *   "definition.actions.My_If.actions.Send_email"
 *   "definition.actions.Agent_Loop.tools.myTool"
 */

/**
 * Get a value at a dot-separated path within an object.
 * Returns undefined if any segment along the path doesn't exist.
 */
export function getValueAtPath(obj: unknown, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Set a value at a dot-separated path within an object (mutates in place).
 * Creates intermediate objects if they don't exist.
 * Returns true if set succeeded.
 */
export function setValueAtPath(obj: unknown, path: string, value: unknown): boolean {
  const parts = path.split('.');
  if (parts.length === 0) return false;

  let current: Record<string, unknown> = obj as Record<string, unknown>;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    if (current[part] == null || typeof current[part] !== 'object') {
      current[part] = {};
    }
    current = current[part] as Record<string, unknown>;
  }

  const finalKey = parts[parts.length - 1]!;
  current[finalKey] = value;
  return true;
}

/**
 * Delete a value at a dot-separated path within an object (mutates in place).
 * Returns true if the value was found and deleted.
 */
export function deleteValueAtPath(obj: unknown, path: string): boolean {
  const parts = path.split('.');
  if (parts.length === 0) return false;

  let current: Record<string, unknown> = obj as Record<string, unknown>;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    if (current[part] == null || typeof current[part] !== 'object') return false;
    current = current[part] as Record<string, unknown>;
  }

  const finalKey = parts[parts.length - 1]!;
  if (finalKey in current) {
    delete current[finalKey];
    return true;
  }
  return false;
}

/**
 * Resolve the JSON path for a node within a workflow definition.
 * Searches recursively through actions (including If/Foreach/Switch/Scope/Until/Agent containers)
 * and triggers. Returns the full dot-path from the workflow root.
 * 
 * @param workflow - The full workflow definition object
 * @param nodeName - The name of the node to find
 * @param nodeType - Optional hint: 'trigger', 'action', or 'tool'
 * @param parentAgentName - For tools, the name of the parent Agent action
 * @returns The dot-path e.g. "definition.actions.My_If.else.actions.Send_email", or null if not found
 */
export function resolveNodePath(
  workflow: Record<string, unknown>,
  nodeName: string,
  nodeType?: 'trigger' | 'action' | 'tool',
  parentAgentName?: string,
): string | null {
  const definition = workflow.definition as Record<string, unknown> | undefined;
  if (!definition) return null;

  // For tools, go directly to the agent's tools
  if (nodeType === 'tool' && parentAgentName) {
    const actions = definition.actions as Record<string, Record<string, unknown>> | undefined;
    if (actions?.[parentAgentName]?.tools) {
      const tools = actions[parentAgentName].tools as Record<string, unknown>;
      if (tools[nodeName] !== undefined) {
        return `definition.actions.${parentAgentName}.tools.${nodeName}`;
      }
    }
    return null;
  }

  // Check triggers (flat, no nesting)
  if (nodeType !== 'action') {
    const triggers = definition.triggers as Record<string, unknown> | undefined;
    if (triggers && nodeName in triggers) {
      return `definition.triggers.${nodeName}`;
    }
    if (nodeType === 'trigger') return null;
  }

  // Search actions recursively
  const actions = definition.actions as Record<string, unknown> | undefined;
  if (actions) {
    const actionPath = findActionPath(actions, nodeName, 'definition.actions');
    if (actionPath) return actionPath;
  }

  return null;
}

/**
 * Recursively search for an action by name within a nested actions structure.
 * Handles: If (true/else), Foreach, Switch (cases/default), Scope, Until, Agent (tools.*.actions).
 */
function findActionPath(
  actions: Record<string, unknown>,
  name: string,
  basePath: string,
): string | null {
  // Direct match at this level
  if (name in actions) {
    return `${basePath}.${name}`;
  }

  // Search nested containers
  for (const [actionName, actionValue] of Object.entries(actions)) {
    const action = actionValue as Record<string, unknown>;
    if (!action || typeof action !== 'object') continue;

    const actionType = action.type as string | undefined;

    // If action: true branch (.actions) and false branch (.else.actions)
    if (actionType === 'If') {
      if (action.actions) {
        const found = findActionPath(action.actions as Record<string, unknown>, name, `${basePath}.${actionName}.actions`);
        if (found) return found;
      }
      const elseBlock = action.else as Record<string, unknown> | undefined;
      if (elseBlock?.actions) {
        const found = findActionPath(elseBlock.actions as Record<string, unknown>, name, `${basePath}.${actionName}.else.actions`);
        if (found) return found;
      }
    }

    // Foreach action: .actions
    if (actionType === 'Foreach') {
      if (action.actions) {
        const found = findActionPath(action.actions as Record<string, unknown>, name, `${basePath}.${actionName}.actions`);
        if (found) return found;
      }
    }

    // Switch action: .cases.<caseName>.actions and .default.actions
    if (actionType === 'Switch') {
      const cases = action.cases as Record<string, Record<string, unknown>> | undefined;
      if (cases) {
        for (const [caseName, caseData] of Object.entries(cases)) {
          if (caseData.actions) {
            const found = findActionPath(caseData.actions as Record<string, unknown>, name, `${basePath}.${actionName}.cases.${caseName}.actions`);
            if (found) return found;
          }
        }
      }
      const defaultCase = action.default as Record<string, unknown> | undefined;
      if (defaultCase?.actions) {
        const found = findActionPath(defaultCase.actions as Record<string, unknown>, name, `${basePath}.${actionName}.default.actions`);
        if (found) return found;
      }
    }

    // Scope action: .actions (same as Foreach)
    if (actionType === 'Scope') {
      if (action.actions) {
        const found = findActionPath(action.actions as Record<string, unknown>, name, `${basePath}.${actionName}.actions`);
        if (found) return found;
      }
    }

    // Until action: .actions
    if (actionType === 'Until') {
      if (action.actions) {
        const found = findActionPath(action.actions as Record<string, unknown>, name, `${basePath}.${actionName}.actions`);
        if (found) return found;
      }
    }

    // Agent action: .tools.<toolName>.actions (inner workflow actions)
    if (actionType === 'Agent' && action.tools) {
      const tools = action.tools as Record<string, Record<string, unknown>>;
      for (const [, toolData] of Object.entries(tools)) {
        if (toolData.actions) {
          const found = findActionPath(toolData.actions as Record<string, unknown>, name, `${basePath}.${actionName}.actions`);
          if (found) return found;
        }
      }
    }
  }

  return null;
}
